
 // =========================================================
// LA ESQUINA DEL PARQUÉS
// ProductsManager.jsx
// CATÁLOGO CENTRAL · INVENTARIO · QR · CÓDIGO DE BARRAS
// React + Django REST Framework
// =========================================================

import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Barcode as BarcodeIcon,
  Camera,
  CheckCircle2,
  ChevronDown,
  Edit3,
  Eye,
  EyeOff,
  History,
  ImagePlus,
  LoaderCircle,
  Package,
  PackageCheck,
  PackagePlus,
  Plus,
  RefreshCw,
  ScanSearch,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  Tag,
  PackageSearch,
  Upload,
  Warehouse,
  X,
  XCircle,
} from "lucide-react";

import { QRCodeSVG } from "qrcode.react";
import Barcode from "react-barcode";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import ProfilePageLayout from "../../components/profile/ProfilePageLayout";
import "../../styles/products-manager.css";

// =========================================================
// CONFIGURACIÓN API
// =========================================================

const API_BASE_URL = (
  import.meta.env.VITE_API_URL ||
  (
    import.meta.env.PROD
      ? "https://api.laesquinadelparques.com/api"
      : "http://localhost:8000/api"
  )
).replace(/\/+$/, "");

const PRODUCTS_ENDPOINT = "/products/";
const STORES_ENDPOINT = "/stores/";
const CATEGORIES_ENDPOINT = "/categories/";
const INVENTORY_ENDPOINT = "/inventory/";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

const EMPTY_FORM = {
  store: "",
  category: "",
  name: "",
  description: "",
  sku: "",
  barcode: "",
  cost_price: "",
  profit_percentage: "30",
  price: "",
  compare_price: "",
  is_active: true,
  is_featured: false,
  original_image: null,
  image: null,
};

// =========================================================
// UTILIDADES
// =========================================================

function readAuth() {
  try {
    const raw = localStorage.getItem("auth");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readToken() {
  const auth = readAuth();

  return (
    auth?.access ||
    auth?.access_token ||
    auth?.token ||
    null
  );
}

function normalizeList(data, depth = 0) {
  if (Array.isArray(data)) return data;

  if (!data || typeof data !== "object" || depth > 5) {
    return [];
  }

  const possibleLists = [
    data.results,
    data.data,
    data.items,
    data.stores,
    data.products,
    data.categories,
    data.inventory,
    data.records,
    data.payload,
  ];

  for (const value of possibleLists) {
    if (Array.isArray(value)) return value;

    if (value && typeof value === "object") {
      const nested = normalizeList(value, depth + 1);

      if (nested.length) return nested;
    }
  }

  return [];
}

function money(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function number(value) {
  return new Intl.NumberFormat("es-CO").format(
    Number(value || 0)
  );
}

function imageUrl(value) {
  if (!value) return "";

  if (typeof value === "object") {
    value = value.url || value.image || value.src || "";
  }

  if (typeof value !== "string" || !value) return "";

  if (/^https?:\/\//i.test(value)) return value;

  if (value.startsWith("/")) {
    try {
      return `${new URL(API_BASE_URL).origin}${value}`;
    } catch {
      return value;
    }
  }

  return value;
}

function getErrorMessage(data, fallback) {
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.message === "string") return data.message;
  if (typeof data === "string" && data.trim()) return data;

  if (data && typeof data === "object") {
    const key = Object.keys(data)[0];
    const value = key ? data[key] : null;

    if (Array.isArray(value) && value.length) {
      return `${key}: ${value[0]}`;
    }

    if (typeof value === "string") {
      return `${key}: ${value}`;
    }
  }

  return fallback;
}

function getProductId(product) {
  return product?.id ?? product?.pk ?? null;
}

function getProductName(product) {
  return (
    product?.name ||
    product?.product_name ||
    "Producto sin nombre"
  );
}

function getProductImage(product) {
  return (
    imageUrl(product?.image) ||
    imageUrl(product?.catalog_image) ||
    imageUrl(product?.primary_image) ||
    imageUrl(product?.catalog_images?.[0]?.image) ||
    imageUrl(product?.catalog_gallery?.[0]?.image) ||
    ""
  );
}

function getCategoryName(product, categories) {
  if (product?.category_name) return product.category_name;

  const id =
    typeof product?.category === "object"
      ? product.category?.id
      : product?.category;

  return (
    categories.find(
      (item) => String(item.id ?? item.pk) === String(id)
    )?.name || "Sin categoría"
  );
}

function getStoreName(product, stores) {
  if (product?.store_name) return product.store_name;

  const id =
    typeof product?.store === "object"
      ? product.store?.id
      : product?.store;

  const found = stores.find(
    (item) => String(item.id ?? item.pk) === String(id)
  );

  return (
    found?.name ||
    found?.store_name ||
    found?.business_name ||
    found?.title ||
    `Tienda #${id || "—"}`
  );
}

function getInventoryForProduct(product, inventory) {
  const productId = getProductId(product);

  return inventory.find((item) => {
    const id =
      typeof item.product === "object"
        ? item.product?.id
        : item.product;

    return String(id) === String(productId);
  }) || null;
}

function getStockValues(product, inventory) {
  const record = getInventoryForProduct(product, inventory);

  const quantity = Number(
    record?.quantity ??
    product?.inventory?.quantity ??
    product?.stock ??
    0
  );

  const reserved = Number(
    record?.reserved_quantity ??
    record?.reserved_stock ??
    product?.inventory?.reserved_quantity ??
    product?.reserved_stock ??
    0
  );

  const available = Number(
    record?.available_quantity ??
    product?.available_stock ??
    Math.max(0, quantity - reserved)
  );

  const low = Boolean(
    record?.low_stock ??
    product?.is_low_stock ??
    false
  );

  return {
    quantity,
    reserved,
    available,
    low,
    empty: Boolean(record?.out_of_stock) || available <= 0,
  };
}

function validateImage(file) {
  if (!file) return "Selecciona una imagen.";

  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    return "Selecciona una imagen JPG, PNG o WEBP.";
  }

  if (file.size > MAX_IMAGE_SIZE) {
    return "La imagen no puede superar los 10 MB.";
  }

  return "";
}

function getProductCode(product) {
  return String(
    product?.barcode ||
    product?.sku ||
    product?.id ||
    product?.pk ||
    ""
  ).trim();
}

// =========================================================
// COMPONENTE PRINCIPAL
// =========================================================

export default function ProductsManager() {
  const navigate = useNavigate();

  const originalInputRef = useRef(null);
  const catalogInputRef = useRef(null);
  const scannerInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanFrameRef = useRef(null);
  const scanBusyRef = useRef(false);
  const detectedRef = useRef(false);

  const [activeTab, setActiveTab] = useState("catalog");

  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [inventory, setInventory] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingStatus, setChangingStatus] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [storesError, setStoresError] = useState("");
  const [loadingStores, setLoadingStores] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [storeFilter, setStoreFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [previews, setPreviews] = useState({
    original_image: "",
    image: "",
  });

  // -------------------------------------------------------
  // ESCÁNER
  // -------------------------------------------------------

  const [scannerStore, setScannerStore] = useState("");
  const [scannerImage, setScannerImage] = useState(null);
  const [scannerPreview, setScannerPreview] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [detectorSupported, setDetectorSupported] = useState(false);
  const [scannerMessage, setScannerMessage] = useState("");
  const [scannerError, setScannerError] = useState("");
  const [barcodeValue, setBarcodeValue] = useState("");
  const [detectedFormat, setDetectedFormat] = useState("");
  const [scannerSearch, setScannerSearch] = useState("");
  const [scannerCandidates, setScannerCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);

  // =======================================================
  // PETICIONES API
  // =======================================================

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    const token = readToken();

    if (!token) {
      navigate("/login", { replace: true });

      throw new Error(
        "Debes iniciar sesión para administrar los productos."
      );
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };

    if (
      options.body &&
      !(options.body instanceof FormData) &&
      !headers["Content-Type"]
    ) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(
      `${API_BASE_URL}${endpoint}`,
      {
        ...options,
        headers,
      }
    );

    if (response.status === 401) {
      localStorage.removeItem("auth");
      localStorage.removeItem("user");
      window.dispatchEvent(new Event("authUpdated"));

      navigate("/login", { replace: true });

      throw new Error(
        "Tu sesión expiró. Inicia sesión nuevamente."
      );
    }

    const contentType = response.headers.get("content-type") || "";
    let data = null;

    if (response.status !== 204) {
      if (contentType.includes("application/json")) {
        data = await response.json().catch(() => null);
      } else {
        data = await response.text().catch(() => "");
      }
    }

    if (!response.ok) {
      throw new Error(
        getErrorMessage(
          data,
          `Error ${response.status} al consultar ${endpoint}.`
        )
      );
    }

    return data;
  }, [navigate]);

  // =======================================================
  // CARGA DE DATOS
  // =======================================================

  const loadProducts = useCallback(async () => {
    const data = await apiRequest(PRODUCTS_ENDPOINT);
    setProducts(normalizeList(data));
  }, [apiRequest]);

  const loadStores = useCallback(async () => {
    setLoadingStores(true);
    setStoresError("");

    try {
      const data = await apiRequest(STORES_ENDPOINT);

      const list = normalizeList(data).filter((item) => {
        const id = item?.id ?? item?.pk;
        return id !== null && id !== undefined && String(id).trim();
      });

      setStores(list);

      if (!list.length) {
        setStoresError(
          "La API no devolvió tiendas. Verifica GET /api/stores/, los permisos y las tiendas registradas en Django."
        );
      }
    } catch (requestError) {
      setStores([]);
      setStoresError(
        requestError?.message || "No fue posible cargar las tiendas."
      );
    } finally {
      setLoadingStores(false);
    }
  }, [apiRequest]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await apiRequest(CATEGORIES_ENDPOINT);
      setCategories(normalizeList(data));
    } catch (requestError) {
      console.error("Error cargando categorías:", requestError);
      setCategories([]);
    }
  }, [apiRequest]);

  const loadInventory = useCallback(async () => {
    try {
      const data = await apiRequest(INVENTORY_ENDPOINT);
      setInventory(normalizeList(data));
    } catch (requestError) {
      console.error("Error cargando inventario:", requestError);
      setInventory([]);
    }
  }, [apiRequest]);

  const loadAll = useCallback(async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      await Promise.all([
        loadProducts(),
        loadStores(),
        loadCategories(),
        loadInventory(),
      ]);
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No fue posible cargar el catálogo."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    loadProducts,
    loadStores,
    loadCategories,
    loadInventory,
  ]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // =======================================================
  // FILTROS Y MÉTRICAS
  // =======================================================

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();

    return products.filter((product) => {
      const text = [
        product.name,
        product.sku,
        product.barcode,
        product.category_name,
        product.store_name,
        product.description,
        product.id,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLowerCase();

      if (query && !text.includes(query)) return false;

      if (statusFilter === "active" && !product.is_active) return false;
      if (statusFilter === "inactive" && product.is_active) return false;
      if (statusFilter === "featured" && !product.is_featured) return false;

      const productStore =
        typeof product.store === "object"
          ? product.store?.id
          : product.store;

      if (
        storeFilter !== "all" &&
        String(productStore) !== String(storeFilter)
      ) {
        return false;
      }

      const productCategory =
        typeof product.category === "object"
          ? product.category?.id
          : product.category;

      if (
        categoryFilter !== "all" &&
        String(productCategory) !== String(categoryFilter)
      ) {
        return false;
      }

      return true;
    });
  }, [
    products,
    search,
    statusFilter,
    storeFilter,
    categoryFilter,
  ]);

  const summary = useMemo(() => {
    const active = products.filter((item) => Boolean(item.is_active)).length;
    const featured = products.filter((item) => Boolean(item.is_featured)).length;

    const stocks = products.map(
      (product) => getStockValues(product, inventory)
    );

    const lowStock = stocks.filter(
      (stock) => stock.low && !stock.empty
    ).length;

    const outOfStock = stocks.filter(
      (stock) => stock.empty
    ).length;

    return {
      total: products.length,
      active,
      inactive: products.length - active,
      featured,
      lowStock,
      outOfStock,
    };
  }, [products, inventory]);

  const calculatedPrice = useMemo(() => {
    const cost = Number(form.cost_price || 0);
    const profit = Number(form.profit_percentage || 0);

    if (!Number.isFinite(cost) || !Number.isFinite(profit)) return 0;

    return Math.round(cost * (1 + profit / 100));
  }, [form.cost_price, form.profit_percentage]);

  // =======================================================
  // FORMULARIO
  // =======================================================

  const resetPreviews = useCallback(() => {
    setPreviews((previous) => {
      Object.values(previous).forEach((value) => {
        if (value?.startsWith("blob:")) URL.revokeObjectURL(value);
      });

      return {
        original_image: "",
        image: "",
      };
    });
  }, []);

  const openCreateForm = () => {
    resetPreviews();
    setEditingProduct(null);

    setForm({
      ...EMPTY_FORM,
      store: stores.length === 1
        ? String(stores[0].id ?? stores[0].pk)
        : "",
    });

    setError("");
    setNotice("");
    setShowForm(true);
    setActiveTab("form");
  };

  const openEditForm = (product) => {
    resetPreviews();

    const storeId =
      typeof product.store === "object"
        ? product.store?.id
        : product.store;

    const categoryId =
      typeof product.category === "object"
        ? product.category?.id
        : product.category;

    setEditingProduct(product);

    setForm({
      ...EMPTY_FORM,
      store: storeId ? String(storeId) : "",
      category: categoryId ? String(categoryId) : "",
      name: product.name || "",
      description: product.description || "",
      sku: product.sku || "",
      barcode: product.barcode || "",
      cost_price: product.cost_price ?? "",
      profit_percentage: product.profit_percentage ?? "30",
      price: product.price ?? "",
      compare_price: product.compare_price ?? "",
      is_active: Boolean(product.is_active),
      is_featured: Boolean(product.is_featured),
      original_image: null,
      image: null,
    });

    setPreviews({
      original_image: imageUrl(product.original_image),
      image: getProductImage(product),
    });

    setError("");
    setNotice("");
    setShowForm(true);
    setActiveTab("form");
  };

  const closeForm = () => {
    if (saving) return;

    setShowForm(false);
    setEditingProduct(null);
    setForm(EMPTY_FORM);
    resetPreviews();
    setActiveTab("catalog");
  };

  const handleImageChange = (event, field) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateImage(file);

    if (validationError) {
      setError(validationError);
      event.target.value = "";
      return;
    }

    setError("");

    setForm((previous) => ({
      ...previous,
      [field]: file,
    }));

    setPreviews((previous) => {
      if (previous[field]?.startsWith("blob:")) {
        URL.revokeObjectURL(previous[field]);
      }

      return {
        ...previous,
        [field]: URL.createObjectURL(file),
      };
    });
  };

  const saveProduct = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      setError("El nombre del producto es obligatorio.");
      return;
    }

    if (!form.store) {
      setError("Selecciona una tienda.");
      return;
    }

    if (form.cost_price === "" || Number(form.cost_price) < 0) {
      setError("Ingresa un costo válido.");
      return;
    }

    if (
      form.profit_percentage === "" ||
      Number(form.profit_percentage) < 0
    ) {
      setError("Ingresa un porcentaje de ganancia válido.");
      return;
    }

    const payload = new FormData();

    payload.append("store", String(form.store));
    payload.append("name", form.name.trim());
    payload.append("description", form.description || "");
    payload.append("cost_price", String(Number(form.cost_price)));
    payload.append(
      "profit_percentage",
      String(Number(form.profit_percentage))
    );
    payload.append("is_active", String(Boolean(form.is_active)));
    payload.append("is_featured", String(Boolean(form.is_featured)));

    if (form.category) {
      payload.append("category", String(form.category));
    }

    if (form.sku.trim()) {
      payload.append("sku", form.sku.trim());
    }

    if (form.barcode.trim()) {
      payload.append("barcode", form.barcode.trim());
    }

    if (form.compare_price !== "" && form.compare_price !== null) {
      payload.append("compare_price", String(Number(form.compare_price)));
    }

    if (form.original_image instanceof File) {
      payload.append("original_image", form.original_image);
    }

    if (form.image instanceof File) {
      payload.append("image", form.image);
    }

    const productId = getProductId(editingProduct);
    const endpoint = productId
      ? `${PRODUCTS_ENDPOINT}${productId}/`
      : PRODUCTS_ENDPOINT;

    setSaving(true);
    setError("");
    setNotice("");

    try {
      const result = await apiRequest(endpoint, {
        method: productId ? "PATCH" : "POST",
        body: payload,
      });

      setNotice(
        productId
          ? "Producto actualizado correctamente."
          : "Producto creado correctamente."
      );

      setShowForm(false);
      setEditingProduct(null);
      setForm(EMPTY_FORM);
      resetPreviews();
      setActiveTab("catalog");

      await Promise.all([loadProducts(), loadInventory()]);

      if (result && getProductId(result)) {
        setSelectedProduct(result);
      }
    } catch (requestError) {
      setError(
        requestError?.message || "No fue posible guardar el producto."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleProductStatus = async (product) => {
    const productId = getProductId(product);
    if (!productId) return;

    setChangingStatus(productId);
    setError("");
    setNotice("");

    try {
      await apiRequest(`${PRODUCTS_ENDPOINT}${productId}/`, {
        method: "PATCH",
        body: JSON.stringify({
          is_active: !product.is_active,
        }),
      });

      setNotice(
        product.is_active
          ? "Producto desactivado correctamente."
          : "Producto activado correctamente."
      );

      await loadProducts();
    } catch (requestError) {
      setError(
        requestError?.message || "No fue posible cambiar el estado."
      );
    } finally {
      setChangingStatus(null);
    }
  };

  // =======================================================
  // ESCÁNER: CÁMARA Y CÓDIGOS
  // =======================================================

  const stopCamera = useCallback(() => {
    if (scanFrameRef.current) {
      cancelAnimationFrame(scanFrameRef.current);
      scanFrameRef.current = null;
    }

    detectorRef.current = null;
    scanBusyRef.current = false;
    detectedRef.current = false;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraOpen(false);
    setCameraLoading(false);
  }, []);

  const scanCameraFrame = useCallback(async () => {
    if (!cameraOpen || detectedRef.current) return;

    const video = videoRef.current;
    const detector = detectorRef.current;

    if (!video || !detector) return;

    if (video.readyState >= 2 && !scanBusyRef.current) {
      scanBusyRef.current = true;

      try {
        const results = await detector.detect(video);

        if (results.length > 0) {
          const result = results[0];
          const code = String(result.rawValue || "").trim();

          if (code) {
            detectedRef.current = true;

            setBarcodeValue(code);
            setScannerSearch(code);
            setDetectedFormat(result.format || "desconocido");
            setScannerMessage(
              `Código detectado: ${code}. Formato: ${result.format || "desconocido"}.`
            );

            setScannerError("");
            stopCamera();
            return;
          }
        }
      } catch (scanError) {
        console.warn("Error leyendo código:", scanError);
      } finally {
        scanBusyRef.current = false;
      }
    }

    if (!detectedRef.current && cameraOpen) {
      scanFrameRef.current = requestAnimationFrame(scanCameraFrame);
    }
  }, [cameraOpen, stopCamera]);

  const startBarcodeDetection = useCallback(async () => {
    if (!("BarcodeDetector" in window)) {
      setDetectorSupported(false);
      setScannerMessage(
        "Este navegador no admite lectura automática. Puedes buscar manualmente."
      );
      return;
    }

    try {
      const supportedFormats =
        await window.BarcodeDetector.getSupportedFormats();

      const desiredFormats = [
        "qr_code",
        "ean_13",
        "ean_8",
        "upc_a",
        "upc_e",
        "code_128",
        "code_39",
        "codabar",
        "itf",
        "data_matrix",
        "pdf417",
        "aztec",
      ];

      const formats = desiredFormats.filter((format) =>
        supportedFormats.includes(format)
      );

      if (!formats.length) {
        setScannerError(
          "El navegador no ofrece formatos compatibles para escanear."
        );
        return;
      }

      detectorRef.current = new window.BarcodeDetector({ formats });
      setDetectorSupported(true);
      setScannerMessage(
        "Apunta la cámara al código QR o al código de barras."
      );

      if (scanFrameRef.current) {
        cancelAnimationFrame(scanFrameRef.current);
      }

      scanFrameRef.current = requestAnimationFrame(scanCameraFrame);
    } catch (detectorError) {
      console.warn("No se pudo iniciar BarcodeDetector:", detectorError);
      setScannerError("No se pudo inicializar el lector de códigos.");
    }
  }, [scanCameraFrame]);

  const openCamera = async () => {
    setScannerError("");
    setScannerMessage("");
    setCameraLoading(true);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraLoading(false);
      setScannerError(
        "La cámara requiere HTTPS o localhost. También puedes cargar una fotografía."
      );
      return;
    }

    if (!("BarcodeDetector" in window)) {
      setDetectorSupported(false);
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;
      detectedRef.current = false;
      setCameraOpen(true);
      setCameraLoading(false);

      requestAnimationFrame(() => {
        const video = videoRef.current;

        if (video) {
          video.srcObject = stream;

          video.play().catch((playError) => {
            console.warn("No se pudo reproducir el vídeo:", playError);
          });
        }
      });
    } catch (cameraError) {
      setCameraLoading(false);

      setScannerError(
        cameraError?.name === "NotAllowedError"
          ? "No se concedió permiso para usar la cámara."
          : "No fue posible abrir la cámara. Revisa el dispositivo o carga una imagen."
      );
    }
  };

  const captureCameraImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      setScannerError("La cámara todavía no está lista.");
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");

    if (!context) {
      setScannerError("No se pudo procesar la fotografía.");
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        setScannerError("No se pudo generar la imagen.");
        return;
      }

      const file = new File(
        [blob],
        `la-esquina-scan-${Date.now()}.jpg`,
        { type: "image/jpeg" }
      );

      setScannerImage(file);
      setScannerPreview(URL.createObjectURL(file));
      setScannerMessage(
        "Fotografía capturada. Puedes buscar el producto por su código o nombre."
      );
      setScannerError("");
      setScannerCandidates([]);
      setSelectedCandidate(null);

      stopCamera();
    }, "image/jpeg", 0.92);
  };

  const handleScannerFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateImage(file);

    if (validationError) {
      setScannerError(validationError);
      event.target.value = "";
      return;
    }

    if (scannerPreview.startsWith("blob:")) {
      URL.revokeObjectURL(scannerPreview);
    }

    setScannerImage(file);
    setScannerPreview(URL.createObjectURL(file));
    setScannerMessage(
      "Imagen cargada. Puedes buscar manualmente por nombre, SKU o código."
    );
    setScannerError("");
    setScannerCandidates([]);
    setSelectedCandidate(null);
  };

  const clearScannerImage = () => {
    if (scannerPreview.startsWith("blob:")) {
      URL.revokeObjectURL(scannerPreview);
    }

    setScannerImage(null);
    setScannerPreview("");
    setScannerMessage("");
    setScannerError("");
    setBarcodeValue("");
    setDetectedFormat("");
    setScannerCandidates([]);
    setSelectedCandidate(null);
  };

  // =======================================================
  // BÚSQUEDA DE CANDIDATOS
  // =======================================================

  const localCandidates = useMemo(() => {
    const query = (
      scannerSearch.trim() || barcodeValue.trim()
    ).toLowerCase();

    return products.filter((product) => {
      const productStore =
        typeof product.store === "object"
          ? product.store?.id
          : product.store;

      if (
        scannerStore &&
        String(productStore) !== String(scannerStore)
      ) {
        return false;
      }

      if (!query) return false;

      const text = [
        product.name,
        product.sku,
        product.barcode,
        product.description,
        product.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(query);
    }).slice(0, 30);
  }, [products, scannerStore, scannerSearch, barcodeValue]);

  const searchLocalCandidates = () => {
    setScannerError("");
    setSelectedCandidate(null);

    if (!scannerStore) {
      setScannerError("Selecciona la tienda que deseas consultar.");
      return;
    }

    const query = scannerSearch.trim() || barcodeValue.trim();

    if (!query) {
      setScannerError("Introduce un nombre, SKU o código para buscar.");
      return;
    }

    setScannerCandidates(localCandidates);

    setScannerMessage(
      localCandidates.length
        ? `${localCandidates.length} producto(s) encontrados.`
        : "No se encontraron coincidencias. Comprueba el código o el filtro de tienda."
    );

    setScanHistory((previous) => [
      {
        id: `${Date.now()}`,
        date: new Date().toISOString(),
        fileName: scannerImage?.name || "Lectura manual",
        store: stores.find(
          (store) =>
            String(store.id ?? store.pk) === String(scannerStore)
        )?.name || `Tienda #${scannerStore}`,
        query,
        results: localCandidates.length,
        status: "Búsqueda local",
      },
      ...previous,
    ].slice(0, 20));
  };

  const selectScannerCandidate = (product) => {
    setSelectedCandidate(product);
    setScannerMessage(
      `Producto seleccionado: ${getProductName(product)}. La selección no modifica el inventario.`
    );
  };

  const openScannerTab = () => {
    setShowForm(false);
    setActiveTab("scanner");
    setScannerError("");
    setScannerMessage("");

    setScannerStore((previous) => (
      previous ||
      (stores.length === 1
        ? String(stores[0].id ?? stores[0].pk)
        : "")
    ));
  };

  // Liberar recursos de cámara y URL al desmontar.
  useEffect(() => {
    return () => {
      if (scanFrameRef.current) {
        cancelAnimationFrame(scanFrameRef.current);
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      [scannerPreview, previews.image, previews.original_image].forEach(
        (value) => {
          if (value?.startsWith("blob:")) URL.revokeObjectURL(value);
        }
      );
    };
  }, [scannerPreview, previews.image, previews.original_image]);

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <ProfilePageLayout
      title="Productos"
      subtitle="Catálogo, inventario, códigos QR y códigos de barras."
      icon={Package}
    >
      <main className="dc-products-page">
        <button
          type="button"
          className="dc-products-back"
          onClick={() => navigate("/profile")}
        >
          <ArrowLeft size={17} />
          VOLVER A MI PERFIL
        </button>

        {/* HERO */}

        <section className="dc-products-hero">
          <div className="dc-products-hero-copy">
            <span className="dc-products-eyebrow">
              LA ESQUINA DEL PARQUÉS · PRODUCT COMMAND CENTER
            </span>

            <h1>
              GESTIÓN DE
              <span> PRODUCTOS</span>
            </h1>

            <p>
              Administra productos, precios, imágenes, existencias y
              códigos de identificación desde un solo lugar.
            </p>

            <div className="dc-products-hero-actions">
              <button
                type="button"
                className="dc-products-primary"
                onClick={openCreateForm}
              >
                <PackagePlus size={17} />
                NUEVO PRODUCTO
              </button>

              <button
                type="button"
                className="dc-products-secondary"
                onClick={openScannerTab}
              >
                <ScanSearch size={17} />
                ABRIR ESCÁNER
              </button>

              <button
                type="button"
                className="dc-products-secondary"
                onClick={() => loadAll(true)}
                disabled={refreshing}
              >
                {refreshing
                  ? <LoaderCircle size={17} className="dc-products-spin" />
                  : <RefreshCw size={17} />}
                ACTUALIZAR
              </button>
            </div>
          </div>

          <div className="dc-products-hero-art">
            <div className="dc-products-orbit dc-products-orbit-one" />
            <div className="dc-products-orbit dc-products-orbit-two" />

            <div className="dc-products-hero-icon">
              <PackageSearch size={43} strokeWidth={1.4} />
            </div>

            <span className="dc-products-floating dc-products-float-a">
              <BarcodeIcon size={17} /> SKU / QR
            </span>

            <span className="dc-products-floating dc-products-float-b">
              <Sparkles size={17} /> CATÁLOGO
            </span>
          </div>
        </section>

        {/* ALERTAS */}

        {error && (
          <div className="dc-products-alert error" role="alert">
            <XCircle size={19} />
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError("")}
              aria-label="Cerrar error"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {notice && (
          <div className="dc-products-alert success" role="status">
            <CheckCircle2 size={19} />
            <span>{notice}</span>
            <button
              type="button"
              onClick={() => setNotice("")}
              aria-label="Cerrar aviso"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {storesError && (
          <div className="dc-products-alert error" role="alert">
            <XCircle size={19} />
            <span>
              <strong>Problema al cargar las tiendas:</strong>{" "}
              {storesError}
            </span>
            <button
              type="button"
              onClick={loadStores}
              disabled={loadingStores}
              aria-label="Volver a cargar tiendas"
            >
              {loadingStores
                ? <LoaderCircle size={17} className="dc-products-spin" />
                : <RefreshCw size={17} />}
            </button>
          </div>
        )}

        {/* MÉTRICAS */}

        <section className="dc-products-stats">
          <article className="dc-products-stat">
            <div className="dc-products-stat-icon purple">
              <Package size={21} />
            </div>
            <span>PRODUCTOS REGISTRADOS</span>
            <strong>{number(summary.total)}</strong>
            <small>Catálogo central</small>
          </article>

          <article className="dc-products-stat">
            <div className="dc-products-stat-icon green">
              <CheckCircle2 size={21} />
            </div>
            <span>PRODUCTOS ACTIVOS</span>
            <strong>{number(summary.active)}</strong>
            <small>Habilitados en el catálogo</small>
          </article>

          <article className="dc-products-stat">
            <div className="dc-products-stat-icon blue">
              <Sparkles size={21} />
            </div>
            <span>PRODUCTOS DESTACADOS</span>
            <strong>{number(summary.featured)}</strong>
            <small>Marcados como destacados</small>
          </article>

          <article className="dc-products-stat warning">
            <div className="dc-products-stat-icon orange">
              <AlertTriangle size={21} />
            </div>
            <span>ALERTAS DE INVENTARIO</span>
            <strong>{number(summary.lowStock + summary.outOfStock)}</strong>
            <small>
              {number(summary.lowStock)} bajo · {number(summary.outOfStock)} agotado
            </small>
          </article>
        </section>

        {/* PESTAÑAS */}

        <nav className="dc-products-tabs" aria-label="Secciones de productos">
          <button
            type="button"
            className={activeTab === "catalog" ? "active" : ""}
            onClick={() => {
              setActiveTab("catalog");
              setShowForm(false);
            }}
          >
            <Package size={17} /> CATÁLOGO
          </button>

          <button
            type="button"
            className={activeTab === "form" ? "active" : ""}
            onClick={() => {
              if (!showForm) openCreateForm();
            }}
          >
            <Plus size={17} /> NUEVO PRODUCTO
          </button>

          <button
            type="button"
            className={activeTab === "inventory" ? "active" : ""}
            onClick={() => {
              setActiveTab("inventory");
              setShowForm(false);
            }}
          >
            <Warehouse size={17} /> INVENTARIO
          </button>

          <button
            type="button"
            className={activeTab === "scanner" ? "active" : ""}
            onClick={openScannerTab}
          >
            <ScanSearch size={17} /> ESCÁNER
          </button>
        </nav>

        {loading ? (
          <section className="dc-products-panel dc-products-loading">
            <LoaderCircle size={30} className="dc-products-spin" />
            <strong>CARGANDO CATÁLOGO...</strong>
            <span>Conectando con el backend de La Esquina del Parqués</span>
          </section>
        ) : (
          <>
            {/* ESCÁNER */}

            {activeTab === "scanner" && (
              <section className="dc-products-panel dc-products-scanner-panel">
                <div className="dc-products-panel-heading">
                  <div>
                    <span className="dc-products-eyebrow">
                      PRODUCT SCAN CENTER
                    </span>
                    <h2>Escáner de productos</h2>
                    <p>
                      Lee un código con la cámara o busca un producto
                      por su nombre, SKU o código de barras.
                    </p>
                  </div>

                  <span className="dc-products-total-badge">
                    <ScanSearch size={15} />
                    {detectorSupported ? "LECTOR DISPONIBLE" : "ESCÁNER"}
                  </span>
                </div>

                <div className="dc-products-inline-warning">
                  <AlertTriangle size={18} />
                  <span>
                    La lectura automática depende del navegador y de sus
                    formatos compatibles. La búsqueda manual consulta el
                    catálogo cargado desde la API.
                  </span>
                </div>

                <div className="dc-products-scanner-layout">
                  <div className="dc-products-scanner-capture">
                    <div className="dc-products-form-section-title">
                      <Camera size={19} />
                      <div>
                        <h3>Captura de referencia</h3>
                        <p>Usa la cámara o carga una imagen.</p>
                      </div>
                    </div>

                    <div className="dc-products-scanner-preview">
                      {scannerPreview ? (
                        <img
                          src={scannerPreview}
                          alt="Imagen del producto para revisar"
                        />
                      ) : (
                        <div className="dc-products-scanner-empty">
                          <ScanSearch size={45} />
                          <strong>ESPERANDO IMAGEN</strong>
                          <span>La fotografía aparecerá aquí.</span>
                        </div>
                      )}
                    </div>

                    <div className="dc-products-scanner-actions">
                      <button
                        type="button"
                        className="dc-products-primary"
                        onClick={openCamera}
                        disabled={cameraLoading}
                      >
                        {cameraLoading
                          ? <LoaderCircle size={17} className="dc-products-spin" />
                          : <Camera size={17} />}
                        ABRIR CÁMARA
                      </button>

                      <button
                        type="button"
                        className="dc-products-secondary"
                        onClick={() => scannerInputRef.current?.click()}
                      >
                        <Upload size={17} /> CARGAR IMAGEN
                      </button>

                      <button
                        type="button"
                        className="dc-products-secondary"
                        onClick={clearScannerImage}
                        disabled={!scannerImage}
                      >
                        <X size={16} /> LIMPIAR
                      </button>

                      <input
                        ref={scannerInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        capture="environment"
                        className="dc-products-hidden-input"
                        onChange={handleScannerFile}
                      />

                      <canvas
                        ref={canvasRef}
                        className="dc-products-hidden-input"
                      />
                    </div>

                    {scannerImage && (
                      <div className="dc-products-scanner-file">
                        <CheckCircle2 size={16} />
                        <span>{scannerImage.name}</span>
                        <small>
                          {(scannerImage.size / 1024).toFixed(0)} KB
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="dc-products-scanner-review">
                    <div className="dc-products-form-section-title">
                      <PackageSearch size={19} />
                      <div>
                        <h3>Buscar producto</h3>
                        <p>Filtra por tienda, nombre, SKU o código.</p>
                      </div>
                    </div>

                    <label className="dc-products-field">
                      <span>TIENDA *</span>
                      <select
                        value={scannerStore}
                        onChange={(event) => setScannerStore(event.target.value)}
                      >
                        <option value="">Seleccionar tienda</option>
                        {stores.map((store) => {
                          const id = store.id ?? store.pk;
                          const name =
                            store.name ||
                            store.store_name ||
                            store.business_name ||
                            store.title ||
                            `Tienda #${id}`;

                          return (
                            <option key={String(id)} value={String(id)}>
                              {name}
                            </option>
                          );
                        })}
                      </select>
                    </label>

                    <label className="dc-products-field">
                      <span>NOMBRE, SKU O CÓDIGO</span>
                      <input
                        type="search"
                        value={scannerSearch}
                        onChange={(event) => setScannerSearch(event.target.value)}
                        placeholder="Nombre, SKU o código"
                      />
                    </label>

                    <label className="dc-products-field">
                      <span>CÓDIGO LEÍDO</span>
                      <input
                        type="text"
                        value={barcodeValue}
                        onChange={(event) => {
                          setBarcodeValue(event.target.value);
                          setScannerSearch(event.target.value);
                        }}
                        placeholder="Escanea o escribe el código"
                      />
                      {detectedFormat && (
                        <small>Formato detectado: {detectedFormat}</small>
                      )}
                    </label>

                    <button
                      type="button"
                      className="dc-products-primary"
                      onClick={searchLocalCandidates}
                    >
                      <Search size={17} /> BUSCAR EN EL CATÁLOGO
                    </button>

                    {scannerError && (
                      <div className="dc-products-alert error" role="alert">
                        <XCircle size={17} />
                        <span>{scannerError}</span>
                      </div>
                    )}

                    {scannerMessage && (
                      <div className="dc-products-alert success" role="status">
                        <Activity size={17} />
                        <span>{scannerMessage}</span>
                      </div>
                    )}

                    {selectedCandidate && (
                      <div className="dc-products-scanner-selected">
                        <CheckCircle2 size={19} />
                        <div>
                          <strong>PRODUCTO SELECCIONADO</strong>
                          <span>{getProductName(selectedCandidate)}</span>
                          <small>
                            SKU: {selectedCandidate.sku || "Sin SKU"} · ID: {getProductId(selectedCandidate)}
                          </small>
                        </div>
                        <button
                          type="button"
                          className="dc-products-secondary"
                          onClick={() => setSelectedProduct(selectedCandidate)}
                        >
                          <Eye size={15} /> VER
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dc-products-scanner-results">
                  <div className="dc-products-panel-heading">
                    <div>
                      <span className="dc-products-eyebrow">
                        CANDIDATE REVIEW
                      </span>
                      <h2>Resultados</h2>
                    </div>
                    <span className="dc-products-total-badge">
                      {scannerCandidates.length} RESULTADOS
                    </span>
                  </div>

                  {scannerCandidates.length === 0 ? (
                    <div className="dc-products-empty">
                      <PackageSearch size={43} />
                      <strong>NO HAY RESULTADOS</strong>
                      <span>
                        Introduce un código o nombre y realiza una búsqueda.
                      </span>
                    </div>
                  ) : (
                    <div className="dc-products-scanner-candidates">
                      {scannerCandidates.map((product) => {
                        const selected =
                          String(getProductId(selectedCandidate)) ===
                          String(getProductId(product));

                        return (
                          <article
                            key={getProductId(product)}
                            className={`dc-products-scanner-candidate ${selected ? "selected" : ""}`}
                          >
                            <div className="dc-products-product-image">
                              {getProductImage(product)
                                ? (
                                  <img
                                    src={getProductImage(product)}
                                    alt={getProductName(product)}
                                    loading="lazy"
                                  />
                                )
                                : <Package size={25} />}
                            </div>

                            <div className="dc-products-scanner-candidate-info">
                              <strong>{getProductName(product)}</strong>
                              <span>SKU: {product.sku || "Sin SKU"}</span>
                              <span>{getStoreName(product, stores)}</span>
                              <b>{money(product.price)}</b>
                            </div>

                            <button
                              type="button"
                              className={selected ? "dc-products-primary" : "dc-products-secondary"}
                              onClick={() => selectScannerCandidate(product)}
                            >
                              {selected
                                ? <CheckCircle2 size={16} />
                                : <PackageCheck size={16} />}
                              {selected ? "SELECCIONADO" : "REVISAR"}
                            </button>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="dc-products-scanner-history">
                  <div className="dc-products-panel-heading">
                    <div>
                      <span className="dc-products-eyebrow">SESSION HISTORY</span>
                      <h2><History size={20} /> Historial de esta sesión</h2>
                      <p>
                        El historial de esta sección se mantiene en memoria
                        mientras el componente esté montado.
                      </p>
                    </div>
                  </div>

                  {scanHistory.length === 0 ? (
                    <div className="dc-products-empty compact">
                      <History size={32} />
                      <strong>SIN REVISIONES</strong>
                      <span>Las búsquedas aparecerán aquí.</span>
                    </div>
                  ) : (
                    <div className="dc-products-table-wrap">
                      <table className="dc-products-table">
                        <thead>
                          <tr>
                            <th>FECHA</th>
                            <th>ORIGEN</th>
                            <th>TIENDA</th>
                            <th>BÚSQUEDA</th>
                            <th>RESULTADOS</th>
                            <th>ESTADO</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scanHistory.map((item) => (
                            <tr key={item.id}>
                              <td>{new Date(item.date).toLocaleString("es-CO")}</td>
                              <td>{item.fileName}</td>
                              <td>{item.store}</td>
                              <td>{item.query}</td>
                              <td>{item.results}</td>
                              <td>
                                <span className="dc-products-status active">
                                  {item.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* CATÁLOGO */}

            {activeTab === "catalog" && (
              <section className="dc-products-panel">
                <div className="dc-products-panel-heading">
                  <div>
                    <span className="dc-products-eyebrow">PRODUCT MANAGEMENT</span>
                    <h2>Catálogo de productos</h2>
                    <p>Consulta, filtra, crea y edita productos.</p>
                  </div>
                  <span className="dc-products-total-badge">
                    {filteredProducts.length} PRODUCTOS
                  </span>
                </div>

                <div className="dc-products-toolbar">
                  <label className="dc-products-search">
                    <Search size={18} />
                    <input
                      type="search"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Buscar nombre, SKU, código o descripción..."
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        aria-label="Limpiar búsqueda"
                      >
                        <X size={15} />
                      </button>
                    )}
                  </label>

                  <label className="dc-products-filter">
                    <span>ESTADO</span>
                    <select
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                    >
                      <option value="all">Todos</option>
                      <option value="active">Activos</option>
                      <option value="inactive">Inactivos</option>
                      <option value="featured">Destacados</option>
                    </select>
                    <ChevronDown size={15} />
                  </label>

                  <label className="dc-products-filter">
                    <span>TIENDA</span>
                    <select
                      value={storeFilter}
                      onChange={(event) => setStoreFilter(event.target.value)}
                    >
                      <option value="all">Todas</option>
                      {stores.map((store) => {
                        const id = store.id ?? store.pk;
                        const name =
                          store.name ||
                          store.store_name ||
                          store.business_name ||
                          store.title ||
                          `Tienda #${id}`;

                        return (
                          <option key={String(id)} value={String(id)}>
                            {name}
                          </option>
                        );
                      })}
                    </select>
                    <ChevronDown size={15} />
                  </label>

                  <label className="dc-products-filter">
                    <span>CATEGORÍA</span>
                    <select
                      value={categoryFilter}
                      onChange={(event) => setCategoryFilter(event.target.value)}
                    >
                      <option value="all">Todas</option>
                      {categories.map((category) => (
                        <option
                          key={category.id ?? category.pk}
                          value={String(category.id ?? category.pk)}
                        >
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={15} />
                  </label>
                </div>

                {filteredProducts.length === 0 ? (
                  <div className="dc-products-empty">
                    <PackageSearch size={43} />
                    <strong>NO HAY PRODUCTOS PARA MOSTRAR</strong>
                    <span>Comprueba los filtros o registra un producto.</span>
                    <button
                      type="button"
                      className="dc-products-primary"
                      onClick={openCreateForm}
                    >
                      <Plus size={17} /> CREAR PRODUCTO
                    </button>
                  </div>
                ) : (
                  <div className="dc-products-table-wrap">
                    <table className="dc-products-table">
                      <thead>
                        <tr>
                          <th>PRODUCTO</th>
                          <th>TIENDA / CATEGORÍA</th>
                          <th>PRECIO</th>
                          <th>INVENTARIO</th>
                          <th>ESTADO</th>
                          <th>ACCIONES</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.map((product) => {
                          const id = getProductId(product);
                          const stock = getStockValues(product, inventory);
                          const photo = getProductImage(product);

                          return (
                            <tr key={id}>
                              <td>
                                <div className="dc-products-product-cell">
                                  <div className="dc-products-product-image">
                                    {photo
                                      ? <img src={photo} alt={getProductName(product)} loading="lazy" />
                                      : <Package size={24} />}
                                  </div>

                                  <div className="dc-products-product-info">
                                    <strong>{getProductName(product)}</strong>
                                    <span>SKU: {product.sku || "PENDIENTE"}</span>
                                    <small>
                                      Código: {product.barcode || "Sin código"}
                                    </small>
                                    <small>
                                      ID: {id ?? "—"}
                                      {product.is_featured ? " · DESTACADO" : ""}
                                    </small>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <div className="dc-products-store-category">
                                  <span><Store size={13} /> {getStoreName(product, stores)}</span>
                                  <span><Tag size={13} /> {getCategoryName(product, categories)}</span>
                                </div>
                              </td>

                              <td>
                                <div className="dc-products-price-cell">
                                  <strong>{money(product.price)}</strong>
                                  {Number(product.compare_price || 0) > Number(product.price || 0) && (
                                    <del>{money(product.compare_price)}</del>
                                  )}
                                  {product.profit_percentage !== undefined && (
                                    <small>Margen: {number(product.profit_percentage)}%</small>
                                  )}
                                </div>
                              </td>

                              <td>
                                <div className="dc-products-stock-cell">
                                  <strong>{number(stock.available)}</strong>
                                  <span>
                                    {number(stock.quantity)} unidades · {number(stock.reserved)} reservadas
                                  </span>
                                  {stock.empty
                                    ? <small className="dc-products-stock-empty">AGOTADO</small>
                                    : stock.low
                                      ? <small className="dc-products-stock-low">STOCK BAJO</small>
                                      : <small className="dc-products-stock-good">DISPONIBLE</small>}
                                </div>
                              </td>

                              <td>
                                <div className="dc-products-status-stack">
                                  <span className={`dc-products-status ${product.is_active ? "active" : "inactive"}`}>
                                    {product.is_active
                                      ? <CheckCircle2 size={13} />
                                      : <EyeOff size={13} />}
                                    {product.is_active ? "ACTIVO" : "INACTIVO"}
                                  </span>
                                  {product.is_featured && (
                                    <span className="dc-products-featured">
                                      <Sparkles size={12} /> DESTACADO
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td>
                                <div className="dc-products-actions">
                                  <button
                                    type="button"
                                    className="dc-products-icon-button"
                                    title="Ver detalle, QR y código de barras"
                                    aria-label={`Ver ${getProductName(product)}`}
                                    onClick={() => setSelectedProduct(product)}
                                  >
                                    <Eye size={16} />
                                  </button>

                                  <button
                                    type="button"
                                    className="dc-products-icon-button edit"
                                    title="Editar producto"
                                    aria-label={`Editar ${getProductName(product)}`}
                                    onClick={() => openEditForm(product)}
                                  >
                                    <Edit3 size={16} />
                                  </button>

                                  <button
                                    type="button"
                                    className="dc-products-icon-button toggle"
                                    title={product.is_active ? "Desactivar" : "Activar"}
                                    aria-label={product.is_active ? "Desactivar producto" : "Activar producto"}
                                    disabled={changingStatus === id}
                                    onClick={() => toggleProductStatus(product)}
                                  >
                                    {changingStatus === id
                                      ? <LoaderCircle size={16} className="dc-products-spin" />
                                      : product.is_active
                                        ? <EyeOff size={16} />
                                        : <CheckCircle2 size={16} />}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {/* FORMULARIO DE PRODUCTO */}

            {activeTab === "form" && showForm && (
              <section className="dc-products-panel dc-products-form-panel">
                <div className="dc-products-panel-heading">
                  <div>
                    <span className="dc-products-eyebrow">
                      {editingProduct ? "PRODUCT EDITOR" : "PRODUCT CREATOR"}
                    </span>
                    <h2>
                      {editingProduct ? "Editar producto" : "Registrar producto"}
                    </h2>
                    <p>Completa los datos comerciales y de identificación.</p>
                  </div>

                  <button
                    type="button"
                    className="dc-products-secondary"
                    onClick={closeForm}
                    disabled={saving}
                  >
                    <X size={16} /> CERRAR
                  </button>
                </div>

                <form className="dc-products-form" onSubmit={saveProduct}>
                  <div className="dc-products-form-section">
                    <div className="dc-products-form-section-title">
                      <Package size={18} />
                      <div>
                        <h3>Información del producto</h3>
                        <p>Identificación y organización del catálogo.</p>
                      </div>
                    </div>

                    <div className="dc-products-form-grid">
                      <label className="dc-products-field">
                        <span>NOMBRE DEL PRODUCTO *</span>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value }))}
                          placeholder="Nombre del producto"
                          maxLength={180}
                          required
                        />
                      </label>

                      <label className="dc-products-field">
                        <span>SKU</span>
                        <input
                          type="text"
                          value={form.sku}
                          onChange={(event) => setForm((previous) => ({ ...previous, sku: event.target.value }))}
                          placeholder="Código interno del producto"
                        />
                      </label>

                      <label className="dc-products-field">
                        <span>CÓDIGO DE BARRAS</span>
                        <input
                          type="text"
                          value={form.barcode}
                          onChange={(event) => setForm((previous) => ({ ...previous, barcode: event.target.value }))}
                          placeholder="EAN, UPC o código interno"
                        />
                        <small>
                          Debe ser aceptado por el serializer de Django.
                        </small>
                      </label>

                      <label className="dc-products-field">
                        <span>TIENDA *</span>
                        <select
                          value={form.store}
                          onChange={(event) => setForm((previous) => ({ ...previous, store: event.target.value }))}
                          required
                        >
                          <option value="">Seleccionar tienda</option>
                          {stores.map((store) => {
                            const id = store.id ?? store.pk;
                            const name =
                              store.name ||
                              store.store_name ||
                              store.business_name ||
                              store.title ||
                              `Tienda #${id}`;

                            return (
                              <option key={String(id)} value={String(id)}>
                                {name}
                              </option>
                            );
                          })}
                        </select>
                      </label>

                      <label className="dc-products-field">
                        <span>CATEGORÍA</span>
                        <select
                          value={form.category}
                          onChange={(event) => setForm((previous) => ({ ...previous, category: event.target.value }))}
                        >
                          <option value="">Sin categoría</option>
                          {categories.map((category) => (
                            <option
                              key={category.id ?? category.pk}
                              value={String(category.id ?? category.pk)}
                            >
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="dc-products-field full">
                        <span>DESCRIPCIÓN</span>
                        <textarea
                          value={form.description}
                          onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))}
                          rows={5}
                          placeholder="Características y detalles del producto..."
                        />
                      </label>
                    </div>
                  </div>

                  <div className="dc-products-form-section">
                    <div className="dc-products-form-section-title">
                      <Activity size={18} />
                      <div>
                        <h3>Configuración comercial</h3>
                        <p>Costos, margen y precio estimado.</p>
                      </div>
                    </div>

                    <div className="dc-products-form-grid">
                      <label className="dc-products-field">
                        <span>COSTO DE COMPRA (COP) *</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={form.cost_price}
                          onChange={(event) => setForm((previous) => ({ ...previous, cost_price: event.target.value }))}
                          placeholder="0"
                          required
                        />
                      </label>

                      <label className="dc-products-field">
                        <span>MARGEN DE GANANCIA (%) *</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.profit_percentage}
                          onChange={(event) => setForm((previous) => ({ ...previous, profit_percentage: event.target.value }))}
                          required
                        />
                      </label>

                      <div className="dc-products-calculated-price">
                        <span>PRECIO ESTIMADO</span>
                        <strong>{money(calculatedPrice)}</strong>
                        <small>Django debe validar el precio definitivo.</small>
                      </div>

                      <label className="dc-products-field">
                        <span>PRECIO DE COMPARACIÓN (COP)</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={form.compare_price}
                          onChange={(event) => setForm((previous) => ({ ...previous, compare_price: event.target.value }))}
                          placeholder="Opcional"
                        />
                      </label>
                    </div>
                  </div>

                  <div className="dc-products-form-section">
                    <div className="dc-products-form-section-title">
                      <ImagePlus size={18} />
                      <div>
                        <h3>Imágenes del producto</h3>
                        <p>Fotografía original e imagen comercial.</p>
                      </div>
                    </div>

                    <div className="dc-products-images-grid">
                      <div className="dc-products-image-editor">
                        <div className="dc-products-image-editor-heading">
                          <strong>FOTOGRAFÍA ORIGINAL</strong>
                          <span>Referencia interna</span>
                        </div>

                        <button
                          type="button"
                          className="dc-products-image-upload"
                          onClick={() => originalInputRef.current?.click()}
                        >
                          {previews.original_image
                            ? <img src={previews.original_image} alt="Fotografía original" />
                            : <>
                                <Upload size={27} />
                                <strong>SUBIR FOTO ORIGINAL</strong>
                                <span>JPG, PNG o WEBP · Máximo 10 MB</span>
                              </>}
                        </button>

                        <input
                          ref={originalInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="dc-products-hidden-input"
                          onChange={(event) => handleImageChange(event, "original_image")}
                        />

                        <small className="dc-products-image-note">
                          La visibilidad pública depende de los permisos del backend.
                        </small>
                      </div>

                      <div className="dc-products-image-editor">
                        <div className="dc-products-image-editor-heading">
                          <strong>IMAGEN DEL CATÁLOGO</strong>
                          <span>Imagen principal para clientes</span>
                        </div>

                        <button
                          type="button"
                          className="dc-products-image-upload"
                          onClick={() => catalogInputRef.current?.click()}
                        >
                          {previews.image
                            ? <img src={previews.image} alt="Imagen del catálogo" />
                            : <>
                                <ImagePlus size={27} />
                                <strong>SUBIR IMAGEN PRINCIPAL</strong>
                                <span>JPG, PNG o WEBP · Máximo 10 MB</span>
                              </>}
                        </button>

                        <input
                          ref={catalogInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="dc-products-hidden-input"
                          onChange={(event) => handleImageChange(event, "image")}
                        />

                        <small className="dc-products-image-note">
                          Se envía en el campo image.
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="dc-products-form-section">
                    <div className="dc-products-form-section-title">
                      <ShieldCheck size={18} />
                      <div>
                        <h3>Publicación y visibilidad</h3>
                        <p>Configura el estado del producto.</p>
                      </div>
                    </div>

                    <div className="dc-products-toggles">
                      <label className="dc-products-toggle">
                        <input
                          type="checkbox"
                          checked={form.is_active}
                          onChange={(event) => setForm((previous) => ({ ...previous, is_active: event.target.checked }))}
                        />
                        <span className="dc-products-toggle-ui" />
                        <span className="dc-products-toggle-copy">
                          <strong>PRODUCTO ACTIVO</strong>
                          <small>Habilita el producto según las reglas de Django.</small>
                        </span>
                      </label>

                      <label className="dc-products-toggle">
                        <input
                          type="checkbox"
                          checked={form.is_featured}
                          onChange={(event) => setForm((previous) => ({ ...previous, is_featured: event.target.checked }))}
                        />
                        <span className="dc-products-toggle-ui" />
                        <span className="dc-products-toggle-copy">
                          <strong>PRODUCTO DESTACADO</strong>
                          <small>Marca el producto como destacado.</small>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="dc-products-form-actions">
                    <button
                      type="button"
                      className="dc-products-secondary"
                      onClick={closeForm}
                      disabled={saving}
                    >
                      CANCELAR
                    </button>

                    <button
                      type="submit"
                      className="dc-products-primary"
                      disabled={saving}
                    >
                      {saving
                        ? <LoaderCircle size={18} className="dc-products-spin" />
                        : <CheckCircle2 size={18} />}
                      {saving
                        ? "GUARDANDO..."
                        : editingProduct
                          ? "GUARDAR CAMBIOS"
                          : "CREAR PRODUCTO"}
                      {!saving && <ArrowRight size={17} />}
                    </button>
                  </div>
                </form>
              </section>
            )}

            {/* INVENTARIO */}

            {activeTab === "inventory" && (
              <section className="dc-products-panel">
                <div className="dc-products-panel-heading">
                  <div>
                    <span className="dc-products-eyebrow">INVENTORY LINK</span>
                    <h2>Existencias relacionadas</h2>
                    <p>
                      Consulta cantidades registradas por el endpoint de inventario.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="dc-products-secondary"
                    onClick={loadInventory}
                  >
                    <RefreshCw size={16} /> ACTUALIZAR
                  </button>
                </div>

                {products.length === 0 ? (
                  <div className="dc-products-empty">
                    <Warehouse size={40} />
                    <strong>NO HAY PRODUCTOS REGISTRADOS</strong>
                    <span>Registra productos para consultar su inventario.</span>
                  </div>
                ) : (
                  <div className="dc-products-table-wrap">
                    <table className="dc-products-table">
                      <thead>
                        <tr>
                          <th>PRODUCTO</th>
                          <th>TIENDA</th>
                          <th>EXISTENCIA</th>
                          <th>RESERVADO</th>
                          <th>DISPONIBLE</th>
                          <th>ESTADO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((product) => {
                          const stock = getStockValues(product, inventory);

                          return (
                            <tr key={getProductId(product)}>
                              <td>
                                <div className="dc-products-product-cell">
                                  <div className="dc-products-product-image">
                                    {getProductImage(product)
                                      ? <img src={getProductImage(product)} alt={getProductName(product)} loading="lazy" />
                                      : <Package size={22} />}
                                  </div>
                                  <div className="dc-products-product-info">
                                    <strong>{getProductName(product)}</strong>
                                    <span>SKU: {product.sku || "SIN SKU"}</span>
                                  </div>
                                </div>
                              </td>

                              <td>{getStoreName(product, stores)}</td>
                              <td>{number(stock.quantity)}</td>
                              <td>{number(stock.reserved)}</td>
                              <td><strong>{number(stock.available)}</strong></td>

                              <td>
                                {stock.empty
                                  ? <span className="dc-products-status inactive"><XCircle size={13} /> AGOTADO</span>
                                  : stock.low
                                    ? <span className="dc-products-status low"><AlertTriangle size={13} /> STOCK BAJO</span>
                                    : <span className="dc-products-status active"><PackageCheck size={13} /> DISPONIBLE</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {inventory.length === 0 && (
                  <div className="dc-products-inline-warning">
                    <AlertTriangle size={17} />
                    <span>
                      El endpoint de inventario no devolvió registros. Verifica la
                      API antes de interpretar las cantidades como existencias reales.
                    </span>
                  </div>
                )}
              </section>
            )}
          </>
        )}

        <footer className="dc-products-footer">
          <div>
            <ShieldCheck size={17} />
            <span>PRODUCTOS CENTRALIZADOS · LA ESQUINA DEL PARQUÉS</span>
          </div>
          <span>PRECIOS E INVENTARIO VALIDADOS EN EL SERVIDOR</span>
        </footer>
      </main>

      {/* MODAL CÁMARA */}

      {cameraOpen && (
        <div className="dc-products-modal-backdrop" role="presentation">
          <section
            className="dc-products-modal dc-products-camera-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dc-camera-title"
          >
            <header className="dc-products-modal-header">
              <div>
                <span className="dc-products-eyebrow">LIVE SCANNER</span>
                <h2 id="dc-camera-title">Leer código de producto</h2>
              </div>

              <button type="button" onClick={stopCamera} aria-label="Cerrar cámara">
                <X size={21} />
              </button>
            </header>

            <div className="dc-products-camera-frame">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                aria-label="Vista de cámara"
                onLoadedData={startBarcodeDetection}
              />
            </div>

            <p className="dc-products-image-note">
              Centra el código QR o el código de barras en la cámara.
              Cuando el navegador lo reconozca, el código aparecerá en el campo
              de búsqueda.
            </p>

            <div className="dc-products-modal-actions">
              <button
                type="button"
                className="dc-products-secondary"
                onClick={captureCameraImage}
              >
                <Camera size={17} /> CAPTURAR FOTO
              </button>

              <button
                type="button"
                className="dc-products-primary"
                onClick={stopCamera}
              >
                CANCELAR
              </button>
            </div>
          </section>
        </div>
      )}

      {/* MODAL DETALLE Y CÓDIGOS */}

      {selectedProduct && (
        <div
          className="dc-products-modal-backdrop"
          onMouseDown={() => setSelectedProduct(null)}
        >
          <section
            className="dc-products-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dc-product-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="dc-products-modal-header">
              <div>
                <span className="dc-products-eyebrow">PRODUCT DETAIL</span>
                <h2 id="dc-product-modal-title">
                  {getProductName(selectedProduct)}
                </h2>
              </div>

              <button
                type="button"
                onClick={() => setSelectedProduct(null)}
                aria-label="Cerrar detalle"
              >
                <X size={21} />
              </button>
            </header>

            <div className="dc-products-detail-hero">
              <div className="dc-products-detail-image">
                {getProductImage(selectedProduct)
                  ? <img src={getProductImage(selectedProduct)} alt={getProductName(selectedProduct)} />
                  : <Package size={38} />}
              </div>

              <div className="dc-products-detail-main">
                <span className="dc-products-eyebrow">
                  SKU · {selectedProduct.sku || "SIN SKU"}
                </span>
                <strong>{money(selectedProduct.price)}</strong>
                <span>{getStoreName(selectedProduct, stores)}</span>
                <span>{getCategoryName(selectedProduct, categories)}</span>

                <div className="dc-products-detail-badges">
                  <span className={`dc-products-status ${selectedProduct.is_active ? "active" : "inactive"}`}>
                    {selectedProduct.is_active ? "ACTIVO" : "INACTIVO"}
                  </span>
                  {selectedProduct.is_featured && (
                    <span className="dc-products-featured">
                      <Sparkles size={12} /> DESTACADO
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* QR Y CÓDIGO DE BARRAS */}

            <ProductIdentification product={selectedProduct} />

            <div className="dc-products-detail-grid">
              <div>
                <span>ID DEL PRODUCTO</span>
                <strong>{getProductId(selectedProduct) || "—"}</strong>
              </div>
              <div>
                <span>CÓDIGO DE BARRAS</span>
                <strong>{selectedProduct.barcode || "—"}</strong>
              </div>
              <div>
                <span>PRECIO DE COMPARACIÓN</span>
                <strong>{money(selectedProduct.compare_price)}</strong>
              </div>
              <div>
                <span>COSTO</span>
                <strong>{money(selectedProduct.cost_price)}</strong>
              </div>
              <div>
                <span>MARGEN</span>
                <strong>{number(selectedProduct.profit_percentage)}%</strong>
              </div>
              <div>
                <span>VISTAS</span>
                <strong>{number(selectedProduct.views)}</strong>
              </div>
              <div>
                <span>VENTAS</span>
                <strong>{number(selectedProduct.sales_count)}</strong>
              </div>
              <div>
                <span>SELECCIONES</span>
                <strong>{number(selectedProduct.selection_count)}</strong>
              </div>
              <div>
                <span>FECHA DE CREACIÓN</span>
                <strong>
                  {selectedProduct.created_at
                    ? new Date(selectedProduct.created_at).toLocaleDateString("es-CO")
                    : "—"}
                </strong>
              </div>
            </div>

            {selectedProduct.description && (
              <div className="dc-products-detail-description">
                <span>DESCRIPCIÓN</span>
                <p>{selectedProduct.description}</p>
              </div>
            )}

            {selectedProduct.original_image && (
              <div className="dc-products-detail-original">
                <span>FOTOGRAFÍA ORIGINAL</span>
                <img
                  src={imageUrl(selectedProduct.original_image)}
                  alt="Fotografía original del producto"
                />
              </div>
            )}

            {(selectedProduct.catalog_images?.length > 0 ||
              selectedProduct.catalog_gallery?.length > 0) && (
              <div className="dc-products-detail-gallery">
                <span>GALERÍA DEL CATÁLOGO</span>
                <div className="dc-products-gallery-grid">
                  {(selectedProduct.catalog_images ||
                    selectedProduct.catalog_gallery || []).map((item, index) => (
                    <div key={item.id || index}>
                      {imageUrl(item.image || item.url)
                        ? <img src={imageUrl(item.image || item.url)} alt={`Imagen del catálogo ${index + 1}`} />
                        : <ImagePlus size={22} />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="dc-products-modal-actions">
              <button
                type="button"
                className="dc-products-secondary"
                onClick={() => setSelectedProduct(null)}
              >
                CERRAR
              </button>

              <button
                type="button"
                className="dc-products-primary"
                onClick={() => {
                  const product = selectedProduct;
                  setSelectedProduct(null);
                  openEditForm(product);
                }}
              >
                <Edit3 size={17} /> EDITAR PRODUCTO
              </button>
            </div>
          </section>
        </div>
      )}
    </ProfilePageLayout>
  );
}

// =========================================================
// IDENTIFICACIÓN: QR Y CÓDIGO DE BARRAS
// =========================================================

function ProductIdentification({ product }) {
  const code = getProductCode(product);

  const qrValue = JSON.stringify({
    id: getProductId(product),
    sku: product?.sku || "",
    barcode: product?.barcode || "",
    name: getProductName(product),
  });

  if (!code) {
    return (
      <div className="dc-products-inline-warning">
        <AlertTriangle size={17} />
        <span>
          Este producto todavía no tiene SKU, código de barras ni ID disponible.
        </span>
      </div>
    );
  }

  return (
    <section className="dc-products-identification">
      <div className="dc-products-form-section-title">
        <BarcodeIcon size={20} />
        <div>
          <h3>Identificación del producto</h3>
          <p>QR y código de barras para identificar este registro.</p>
        </div>
      </div>

      <div className="dc-products-identification-grid">
        <article className="dc-products-identification-card">
          <span>CÓDIGO QR</span>
          <div className="dc-products-code-preview">
            <QRCodeSVG
              value={qrValue}
              size={180}
              level="M"
              includeMargin
              title={`QR de ${getProductName(product)}`}
            />
          </div>
          <small>ID: {getProductId(product) ?? "—"}</small>
        </article>

        <article className="dc-products-identification-card">
          <span>CÓDIGO DE BARRAS</span>
          <div className="dc-products-code-preview dc-products-barcode-preview">
            <Barcode
              value={code}
              format="CODE128"
              width={2}
              height={75}
              margin={10}
              displayValue
              background="#ffffff"
              lineColor="#111111"
              fontSize={15}
            />
          </div>
          <small>{code}</small>
        </article>
      </div>
    </section>
  );
}
